import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { EmptyState } from "@/components/EmptyState";
import EditorialMarquee from "@/components/Marquee";

const HERO_IMG =
  "https://images.unsplash.com/photo-1599643477877-530eb83abc8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBnb2xkJTIwZ2Vtc3RvbmUlMjBqZXdlbHJ5JTIwbHV4dXJ5JTIwbmVja2xhY2UlMjByaW5nJTIwYnJhY2VsZXQlMjBwZW5kYW50JTIwcHJvZHVjdCUyMHBob3Rvc2hvb3R8ZW58MHx8fHwxNzg5Njg4MDEwfDA&ixlib=rb-4.1.0&q=85";

const MANIFESTO_IMG =
  "https://images.pexels.com/photos/9859643/pexels-photo-9859643.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const CHAPTERS = [
  {
    number: "01",
    heading: "The Alchemy of Fortune",
    body: "Every gemstone is hand-cut and attuned to harbour clarity, protection and prosperity for its bearer.",
  },
  {
    number: "02",
    heading: "Celestial Gemstones",
    body: "Ethically sourced rubies, sapphires and emeralds imbued with timeless cosmic radiance.",
  },
  {
    number: "03",
    heading: "Heritage Craftsmanship",
    body: "Traditional Indian artistry fused with contemporary architectural precision.",
  },
];

const Hero = () => {
  const ref = useRef(null);
  const [heroImg, setHeroImg] = useState(HERO_IMG);
  useEffect(() => {
    api
      .get("/settings/site")
      .then((r) => {
        if (r.data.hero_image) setHeroImg(r.data.hero_image);
      })
      .catch(() => {});
  }, []);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const scrollToManifesto = () => {
    document.getElementById("manifesto")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[92vh] items-center overflow-hidden bg-cream py-16 lg:py-0"
    >
      <div className="hero-glow pointer-events-none absolute -right-40 top-1/2 h-[720px] w-[720px] -translate-y-1/2 rounded-full" />
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:px-16">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="inline-flex items-center gap-3 border border-gold/40 px-4 py-2"
          >
            <span className="h-1.5 w-1.5 rotate-45 bg-gold-accent" />
            <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold-muted">
              Aarmira — Fashion and Fortune
            </span>
          </motion.div>

          <motion.h1
            data-testid="hero-headline-kinetic"
            variants={lineParent}
            initial="hidden"
            animate="show"
            className="mt-8 font-serif text-5xl leading-[1.05] tracking-tight text-espresso sm:text-6xl lg:text-7xl"
          >
            {["Timeless Alchemy", "for the Soul", "& Fortune."].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span variants={lineChild} className="block">
                  {i === 2 ? <span className="italic text-gold-accent">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.9 }}
            className="mt-8 max-w-md text-base leading-relaxed text-espresso/70"
          >
            Gemstone jewellery hand-crafted in India — where every cut stone is set to carry
            light, luck and legacy.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <Link
              to="/shop"
              data-testid="hero-cta-explore-button"
              className="group inline-flex items-center gap-3 bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-espresso transition-all duration-300 hover:bg-gold-accent active:scale-[0.98]"
            >
              Explore the Collection
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <button
              data-testid="hero-cta-manifesto-button"
              onClick={scrollToManifesto}
              className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-espresso/70 transition-colors hover:text-gold-accent"
            >
              Read the Manifesto
              <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-1" />
            </button>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-sm lg:max-w-none"
          >
            <div className="animate-spin-slow pointer-events-none absolute -inset-8 rounded-full border border-gold/25" />
            <div className="pointer-events-none absolute -inset-3 rounded-full border border-gold/10" />
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="arch-frame relative overflow-hidden border border-gold/40 shadow-[0_40px_80px_-30px_rgba(140,109,49,0.45)]"
            >
              <motion.img
                src={heroImg}
                alt="Emerald and gold pendant necklace by Aarmira"
                style={{ y: yImg, scale: 1.15 }}
                className="aspect-[3/4] w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-espresso/25 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -left-6 bottom-10 border border-gold/30 bg-cream/85 px-5 py-4 backdrop-blur-md"
            >
              <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-gold-muted">Hand-cut</p>
              <p className="mt-1 font-serif text-lg text-espresso">Celestial Gemstones</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const SectionHeading = ({ caption, title, testid }) => (
  <motion.div
    variants={fadeUp}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    className="mb-12"
  >
    <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold-muted">{caption}</p>
    <h2
      data-testid={testid}
      className="mt-3 font-serif text-3xl tracking-tight text-espresso sm:text-4xl"
    >
      {title}
    </h2>
  </motion.div>
);

const FeaturedSection = () => {
  const [products, setProducts] = useState(null);
  useEffect(() => {
    api
      .get("/products", { params: { featured: true } })
      .then((r) => setProducts(r.data.products))
      .catch(() => setProducts([]));
  }, []);

  return (
    <section data-testid="featured-products-section" className="bg-cream px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <SectionHeading caption="Curated Treasures" title="Featured Creations" testid="featured-heading" />
        {products === null ? null : products.length === 0 ? (
          <EmptyState
            testid="featured-empty-state"
            title="The vault is being filled"
            body="Our atelier is preparing the first featured creations. Check back shortly — fortune favours the patient."
          />
        ) : (
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {products.slice(0, 8).map((p) => (
              <ProductCard key={p.id} product={p} testid={`featured-product-card-${p.id}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const Manifesto = () => {
  const [manifestoImg, setManifestoImg] = useState(MANIFESTO_IMG);
  useEffect(() => {
    api
      .get("/settings/site")
      .then((r) => {
        if (r.data.manifesto_image) setManifestoImg(r.data.manifesto_image);
      })
      .catch(() => {});
  }, []);
  return (
  <section
    id="manifesto"
    data-testid="manifesto-section"
    className="relative overflow-hidden bg-espresso px-4 py-24 text-cream sm:px-8 lg:px-16 lg:py-32"
  >
    <div className="hero-glow pointer-events-none absolute -left-52 top-0 h-[560px] w-[560px] rounded-full opacity-60" />
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="overflow-hidden rounded-sm border border-gold/25">
            <img
              src={manifestoImg}
              alt="Indian heritage jewellery editorial"
              loading="lazy"
              className="aspect-[4/5] w-full object-cover"
            />
          </div>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.35em] text-gold">
            The Aarmira Manifesto
          </p>
          <h2 className="mt-3 font-serif text-3xl leading-tight tracking-tight sm:text-4xl">
            Three vows, <span className="italic text-gold">one fortune.</span>
          </h2>
        </motion.div>
      </div>
      <div className="flex flex-col justify-center gap-14 lg:col-span-7 lg:pl-10">
        {CHAPTERS.map((c, i) => (
          <motion.div
            key={c.number}
            data-testid={`manifesto-chapter-${c.number}`}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="group flex gap-8 border-b border-cream/10 pb-14 last:border-0 last:pb-0"
          >
            <span className="font-mono text-sm text-gold-muted transition-colors duration-500 group-hover:text-gold">
              {c.number}
            </span>
            <div>
              <h3 className="font-serif text-2xl leading-snug text-cream sm:text-3xl">{c.heading}</h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-cream/60">{c.body}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  );
};

const NewArrivals = () => {
  const [products, setProducts] = useState(null);
  useEffect(() => {
    api
      .get("/products", { params: { new_arrival: true } })
      .then((r) => setProducts(r.data.products))
      .catch(() => setProducts([]));
  }, []);

  return (
    <section data-testid="new-arrivals-section" className="bg-cream-dark/60 px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading caption="Fresh from the Atelier" title="New Arrivals" testid="new-arrivals-heading" />
          <Link
            to="/shop?category=New%20Arrivals"
            data-testid="new-arrivals-view-all"
            className="group mb-12 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-espresso/70 transition-colors hover:text-gold-accent"
          >
            View all
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        {products === null ? null : products.length === 0 ? (
          <EmptyState
            testid="new-arrivals-empty-state"
            title="New pieces arriving soon"
            body="The atelier's latest work is being polished and photographed. Fresh fortune lands here first."
          />
        ) : (
          <div className="no-scrollbar -mx-4 flex snap-x gap-8 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0">
            {products.slice(0, 8).map((p) => (
              <div key={p.id} className="w-[220px] shrink-0 snap-start">
                <ProductCard product={p} testid={`new-arrival-product-card-${p.id}`} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const CtaBand = () => (
  <section className="bg-cream px-4 py-24 sm:px-8 lg:px-16">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="mx-auto flex max-w-5xl flex-col items-center border border-gold/30 px-6 py-16 text-center"
    >
      <span className="h-2 w-2 rotate-45 bg-gold-accent" />
      <h2 className="mt-6 font-serif text-4xl tracking-tight text-espresso sm:text-5xl">
        Wear your <span className="italic text-gold-accent">fortune.</span>
      </h2>
      <p className="mt-4 max-w-md text-sm leading-relaxed text-espresso/60">
        Every Aarmira piece ships across India with secure Razorpay checkout.
      </p>
      <Link
        to="/shop"
        data-testid="cta-band-shop-button"
        className="mt-8 inline-flex items-center gap-3 bg-espresso px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-cream transition-all duration-300 hover:bg-gold hover:text-espresso active:scale-[0.98]"
      >
        Shop the Collection <ArrowRight size={14} />
      </Link>
    </motion.div>
  </section>
);

const Home = () => (
  <main data-testid="home-page">
    <Hero />
    <EditorialMarquee />
    <FeaturedSection />
    <Manifesto />
    <NewArrivals />
    <CtaBand />
  </main>
);

export default Home;
